import { useEffect, useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";

/**
 * Branded, concise delete-confirmation dialog for personal workspaces.
 * Requires the user to type the exact workspace name before the delete
 * button activates. Mirrors GitHub / Vercel / Linear's danger-zone UX.
 */
export function DeleteWorkspaceDialog({ open, onOpenChange, workspaceName, onConfirm }) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  // Reset internal state every time the dialog re-opens so the previous
  // attempt's text doesn't linger.
  useEffect(() => {
    if (open) {
      setTyped("");
      setBusy(false);
    }
  }, [open]);

  const matches = typed.trim() === (workspaceName || "").trim();

  const handleConfirm = async () => {
    if (!matches || busy) return;
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent
        data-testid="delete-workspace-dialog"
        className="rounded-xl border border-[#E5E7EB] bg-white p-0 max-w-md gap-0 dark:bg-[#161618] dark:border-white/10"
      >
        <AlertDialogHeader className="px-6 pt-6 pb-3 space-y-3">
          <div className="flex items-center justify-center w-11 h-11 rounded-lg bg-[#EF4444]/10 text-[#B91C1C] dark:bg-[#EF4444]/15 dark:text-[#FCA5A5]">
            <AlertTriangle size={18} />
          </div>
          <AlertDialogTitle className="font-['Geist'] font-semibold tracking-tight text-2xl leading-tight text-[#0F172A] dark:text-white">
            Delete workspace?
          </AlertDialogTitle>
          <p className="text-sm text-[#475569] leading-relaxed dark:text-white/60">
            This permanently deletes{" "}
            <span className="font-semibold text-[#0F172A] dark:text-white">{workspaceName}</span>{" "}
            and all its projects, tests, runs, and schedules.
          </p>
        </AlertDialogHeader>
        <div className="px-6 pb-2 space-y-1.5">
          <label className="text-xs font-medium text-[#475569] dark:text-white/60">
            Type <span className="font-mono font-semibold text-[#0F172A] dark:text-white">{workspaceName}</span> to confirm
          </label>
          <Input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={workspaceName}
            autoFocus
            data-testid="delete-workspace-confirm-input"
            className="rounded-md border-[#E5E7EB] focus-visible:ring-[#EF4444] dark:bg-[#1F1F23] dark:border-white/10 dark:text-white"
          />
        </div>
        <AlertDialogFooter className="px-6 pb-6 pt-4 flex-row justify-end gap-2 sm:gap-2">
          <AlertDialogCancel
            disabled={busy}
            data-testid="delete-workspace-cancel"
            className="rounded-md border border-[#E5E7EB] bg-white text-[#0F172A] text-sm font-medium h-9 px-4 mt-0 hover:bg-[#F8FAFC] dark:bg-transparent dark:border-white/10 dark:text-white dark:hover:bg-white/[0.06]"
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={!matches || busy}
            onClick={(e) => {
              e.preventDefault();
              handleConfirm();
            }}
            data-testid="delete-workspace-confirm"
            className="rounded-md text-white text-sm font-medium h-9 px-4 bg-[#EF4444] hover:bg-[#DC2626] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            {busy && <Loader2 size={13} className="animate-spin" />}
            Delete workspace
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
